/**
 * Фильтры, сортировка и пагинация страницы результатов поиска (ТЗ юзабилити,
 * UX-03). Логика чистая: на вход — товары из выдачи и параметры адреса,
 * на выход — что показать. Каталожный фильтр сюда не подходит: он строится
 * вокруг одного раздела, а в выдаче поиска разделы перемешаны.
 */

export const SEARCH_PAGE_SIZE = 24;

export type SearchProduct = {
  ID?: string | number;
  NAME?: string;
  IBLOCK_SECTION_ID?: string | number;
  MARKA?: { VALUE?: string; CODE_ALT?: string } | null;
  PROIZVODITEL_DATA?: { VALUE?: string; CODE_ALT?: string } | null;
  CATALOG?: { MIN_PRICE?: number; PRICE?: number; AVAILABLE?: boolean } | null;
  [key: string]: unknown;
};

export type SearchFilterState = {
  marks: string[];
  brands: string[];
  sections: string[];
  onlyAvailable: boolean;
  priceFrom: number | null;
  priceTo: number | null;
  page: number;
};

export type FacetItem = {
  code: string;
  name: string;
  count: number;
  isChecked: boolean;
};

export type SearchFacets = {
  marks: FacetItem[];
  brands: FacetItem[];
  sections: FacetItem[];
  availableCount: number;
  priceMin: number;
  priceMax: number;
};

function toArrayParam(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.flatMap((item) => toArrayParam(item));
  }
  if (typeof value !== "string" || !value) return [];
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function toNumberParam(value: unknown): number | null {
  const raw = Array.isArray(value) ? value[0] : value;
  if (raw === undefined || raw === null || raw === "") return null;
  const num = Number(raw);
  return Number.isFinite(num) ? num : null;
}

function toStringParam(value: unknown): string {
  const raw = Array.isArray(value) ? value[0] : value;
  return typeof raw === "string" ? raw : "";
}

/** Параметры адреса → состояние фильтра. */
export function parseSearchFilterState(
  query: Record<string, unknown>,
): SearchFilterState {
  const pageRaw = toNumberParam(query.page);

  return {
    marks: toArrayParam(query.mark),
    brands: toArrayParam(query.brand),
    sections: toArrayParam(query.section),
    onlyAvailable: toStringParam(query.available) === "1",
    priceFrom: toNumberParam(query.priceFrom),
    priceTo: toNumberParam(query.priceTo),
    page: pageRaw && pageRaw > 0 ? Math.floor(pageRaw) : 1,
  };
}

export function getProductPrice(product: SearchProduct): number {
  const price = product?.CATALOG?.MIN_PRICE ?? product?.CATALOG?.PRICE;
  const num = Number(price);
  return Number.isFinite(num) ? num : 0;
}

export function getProductMark(product: SearchProduct): {
  code: string;
  name: string;
} | null {
  const code = product?.MARKA?.CODE_ALT;
  const name = product?.MARKA?.VALUE;
  if (!code && !name) return null;
  return { code: String(code || name), name: String(name || code) };
}

/**
 * Производитель товара. В PROIZVODITEL лежит только id значения, читаемое имя
 * и код — в PROIZVODITEL_DATA.
 */
export function getProductBrand(product: SearchProduct): {
  code: string;
  name: string;
} | null {
  const code = product?.PROIZVODITEL_DATA?.CODE_ALT;
  const name = product?.PROIZVODITEL_DATA?.VALUE;
  if (!code && !name) return null;
  return { code: String(code || name), name: String(name || code) };
}

function matchesMarks(product: SearchProduct, marks: string[]): boolean {
  if (!marks.length) return true;
  const mark = getProductMark(product);
  return Boolean(mark && marks.includes(mark.code));
}

function matchesBrands(product: SearchProduct, brands: string[]): boolean {
  if (!brands.length) return true;
  const brand = getProductBrand(product);
  return Boolean(brand && brands.includes(brand.code));
}

function matchesSections(product: SearchProduct, sections: string[]): boolean {
  if (!sections.length) return true;
  return sections.includes(String(product?.IBLOCK_SECTION_ID ?? ""));
}

function matchesRest(
  product: SearchProduct,
  state: SearchFilterState,
): boolean {
  if (state.onlyAvailable && !product?.CATALOG?.AVAILABLE) return false;
  const price = getProductPrice(product);
  if (state.priceFrom !== null && price < state.priceFrom) return false;
  if (state.priceTo !== null && price > state.priceTo) return false;
  return true;
}

/** Товары, прошедшие все фильтры. */
export function applySearchFilters<T extends SearchProduct>(
  products: T[],
  state: SearchFilterState,
): T[] {
  return (products || []).filter(
    (product) =>
      matchesMarks(product, state.marks) &&
      matchesBrands(product, state.brands) &&
      matchesSections(product, state.sections) &&
      matchesRest(product, state),
  );
}

/**
 * Счётчик у значения считается по выдаче, отфильтрованной всеми остальными
 * условиями: иначе цифры рядом с чекбоксами не сходятся с тем, что покажет клик.
 */
function productsForFacet<T extends SearchProduct>(
  products: T[],
  state: SearchFilterState,
  skip: "marks" | "brands" | "sections",
): T[] {
  return applySearchFilters(products, {
    ...state,
    marks: skip === "marks" ? [] : state.marks,
    brands: skip === "brands" ? [] : state.brands,
    sections: skip === "sections" ? [] : state.sections,
  });
}

function countBy<T extends SearchProduct>(
  products: T[],
  getValue: (product: T) => { code: string; name: string } | null,
): Map<string, { name: string; count: number }> {
  const counts = new Map<string, { name: string; count: number }>();
  products.forEach((product) => {
    const value = getValue(product);
    if (!value || !value.code) return;
    const current = counts.get(value.code);
    if (current) {
      current.count += 1;
    } else {
      counts.set(value.code, { name: value.name, count: 1 });
    }
  });
  return counts;
}

function toFacetItems(
  counts: Map<string, { name: string; count: number }>,
  checked: string[],
): FacetItem[] {
  return Array.from(counts.entries())
    .map(([code, { name, count }]) => ({
      code,
      name,
      count,
      isChecked: checked.includes(code),
    }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name, "ru"));
}

/** Значения фильтров, которые есть в этой выдаче, со счётчиками. */
export function buildSearchFacets<T extends SearchProduct>(
  products: T[],
  state: SearchFilterState,
  sectionNames: Record<string, string> = {},
): SearchFacets {
  const list = products || [];
  const prices = list.map(getProductPrice).filter((price) => price > 0);
  // Пустой справочник — значит разделы не пришли вовсе (сбой запроса), и
  // отличить скрытый раздел от обычного нечем: тогда показываем все.
  const hasSectionNames = Object.keys(sectionNames || {}).length > 0;

  return {
    marks: toFacetItems(
      countBy(productsForFacet(list, state, "marks"), getProductMark),
      state.marks,
    ),
    brands: toFacetItems(
      countBy(productsForFacet(list, state, "brands"), getProductBrand),
      state.brands,
    ),
    sections: toFacetItems(
      countBy(productsForFacet(list, state, "sections"), (product) => {
        const code = String(product?.IBLOCK_SECTION_ID ?? "");
        if (!code) return null;
        const name = sectionNames[code];
        // Раздела нет в каталожном справочнике — он скрыт. Предлагать его
        // фильтром нельзя, да и подписать нечем: раньше в списке оказывался
        // голый id вида «655». Товары раздела из выдачи при этом не убираем.
        if (!name) return hasSectionNames ? null : { code, name: code };
        return { code, name };
      }),
      state.sections,
    ),
    availableCount: applySearchFilters(list, {
      ...state,
      onlyAvailable: false,
    }).filter((product) => product?.CATALOG?.AVAILABLE).length,
    priceMin: prices.length ? Math.floor(Math.min(...prices)) : 0,
    priceMax: prices.length ? Math.ceil(Math.max(...prices)) : 0,
  };
}

/** Страница выдачи. Номер за пределами выдачи даёт пустой список. */
export function paginateSearchProducts<T>(
  products: T[],
  page: number,
  pageSize: number = SEARCH_PAGE_SIZE,
): T[] {
  const from = (Math.max(1, page) - 1) * pageSize;
  return (products || []).slice(from, from + pageSize);
}

/** Адрес страницы результатов с сохранением остальных параметров. */
export function buildSearchHref(
  q: string,
  params: Record<string, string | number | undefined | null> = {},
): string {
  const search = new URLSearchParams();
  if (q) search.set("q", q);
  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (value === undefined || value === null || value === "") return;
    search.set(key, String(value));
  });
  const query = search.toString();
  return query ? `/search/?${query}` : "/search/";
}
